import {
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  ButtonProps,
  Flex,
  FlexProps,
  IconButton,
  LayoutProps,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuProps,
  Text,
} from "@chakra-ui/react";
import { RiArrowDownSLine, RiCloseLine } from "@remixicon/react";

import {
  IcoAccessCheck,
  IcoFeedback,
  IcoOrganization,
  IcoRemoteConfig,
  IcoTrackingEvent,
  IcoUnknown,
  IcoUser,
} from "@/common/components/Icons";
import MenuDescription from "@/common/components/MenuDescription";
import { DebugEvent } from "@reflag/shared/eventAPI";

export type DebugEventTypeSelectOption = {
  id: DebugEvent["debugEventType"];
  label: string;
  description: string;
  visualization: JSX.Element;
};

const debugEventTypeOptions: DebugEventTypeSelectOption[] = [
  {
    id: "check",
    label: "Check access",
    description: "Client checked if user has access to flag",
    visualization: <IcoAccessCheck color="dimmed" size="sm" />,
  },
  {
    id: "check-config",
    label: "Check config",
    description: "Client checked the value of the flag remote config",
    visualization: <IcoRemoteConfig color="dimmed" size="sm" />,
  },
  {
    id: "user",
    label: "User",
    description: "User identified or updated",
    visualization: <IcoUser color="dimmed" size="sm" />,
  },
  {
    id: "company",
    label: "Company",
    description: "Company identified or updated",
    visualization: <IcoOrganization color="dimmed" size="sm" />,
  },
  {
    id: "feedback",
    label: "Feedback",
    description: "Feedback received",
    visualization: <IcoFeedback color="dimmed" size="sm" />,
  },
  {
    id: "track",
    label: "Track",
    description: "Track events",
    visualization: <IcoTrackingEvent color="dimmed" size="sm" />,
  },
];

export function DebugEventTypeDisplay({
  type,
  showDescription = false,
  labelWidth,
  ...rest
}: FlexProps & {
  type: DebugEvent["debugEventType"];
  showDescription?: boolean;
  labelWidth?: LayoutProps["w"];
}) {
  const { label, description, visualization } = debugEventTypeOptions.find(
    (option) => option.id === type,
  ) ?? {
    label: "Unknown",
    description: "Unknown event type",
    visualization: <IcoUnknown color="dimmed" size="sm" />,
  };

  return (
    <Flex align="center" gap={2} {...rest}>
      {visualization}
      <Text w={labelWidth} whiteSpace="nowrap">
        {label}
      </Text>
      {showDescription && (
        <Text variant="dimmed" whiteSpace="nowrap">
          {description}
        </Text>
      )}
    </Flex>
  );
}

type DebugEventTypeSelectProps = Omit<BoxProps, "children" | "onChange"> & {
  canClear?: boolean;
  leftIcon?: ButtonProps["leftIcon"];
  placeholder?: string;
  value?: DebugEvent["debugEventType"][];
  onChange?: (types: DebugEvent["debugEventType"][]) => void;
  menuProps?: Omit<MenuProps, "children">;
};

export function DebugEventTypeSelect({
  leftIcon,
  value,
  placeholder = "Any event type",
  onChange,
  menuProps,
  ...rest
}: DebugEventTypeSelectProps) {
  return (
    <ButtonGroup {...rest} isAttached>
      <Menu closeOnSelect={false} {...menuProps}>
        <MenuButton
          as={Button}
          color={value ? undefined : "dimmed"}
          leftIcon={leftIcon}
          rightIcon={
            <Box fontSize="xl" mr={-2}>
              <RiArrowDownSLine size={16} />
            </Box>
          }
          variant="input"
        >
          <Flex gap={2}>
            {value && value?.length > 0 ? (
              <>
                {value.map((type) => (
                  <DebugEventTypeDisplay key={type} type={type} />
                ))}
              </>
            ) : (
              <Text color="dimmed">{placeholder}</Text>
            )}
          </Flex>
        </MenuButton>
        {value && value.length > 0 && (
          <IconButton
            aria-label="Clear selection"
            icon={<RiCloseLine size={16} />}
            marginInlineStart={"-1px"}
            variant="outline"
            onClick={() => onChange?.([])}
          />
        )}
        <MenuList>
          <MenuDescription>Filter events by type</MenuDescription>
          <MenuDivider my={0} />
          <MenuOptionGroup
            type="checkbox"
            value={value}
            onChange={(newValue) => {
              onChange?.(newValue as DebugEvent["debugEventType"][]);
            }}
          >
            {debugEventTypeOptions.map(({ id }) => (
              <MenuItemOption key={id} value={id}>
                <DebugEventTypeDisplay
                  labelWidth={24}
                  type={id}
                  showDescription
                />
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
    </ButtonGroup>
  );
}
